import { memo } from 'react';

import { type CustomizationMachineTypes, type PortionOption } from '../../machine';
import { CustomizationIngredientItem } from '../CustomizationIngredientItem';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationModificationItem = memo((props: Props) => {
  const {
    modification,
    quantity,
    isSelected,
    isStepperActive,
    notice,
    upsellPrice,
    withMinHeight,
    assetAspectRatioY,
    assetAspectRatioX,
    variation,
    canAddMore,
    isMutedStateEnabled,
    maxPortionsForGroup,
    isSplitPortion,
    portionOptions,
    hasPortionOptions,
    isUpsellPricingEnabled,
    size,
    dataSet,
    onIncrement,
    onDecrement,
    onRemove,
    onToggleSplitPortion,
    setActiveStepperId,
    resetStepperTimeout,
    clearStepperTimeout,
  } = props;

  // ─────────────────────────────────────────────────────────────────────

  if (!modification.ingredient) return null;

  const { ingredient, outOfStock } = modification;
  const { id, name, asset, kind } = ingredient;

  const isOutOfStock = outOfStock ?? false;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <CustomizationIngredientItem
      key={modification.id}
      id={id}
      kind={kind}
      name={name}
      assetUrl={asset.url}
      assetAspectRatioY={assetAspectRatioY}
      assetAspectRatioX={assetAspectRatioX}
      isOutOfStock={isOutOfStock}
      notice={notice}
      upsellPrice={upsellPrice}
      quantity={quantity}
      canAddMore={canAddMore}
      isSelected={isSelected}
      isSplitPortion={isSplitPortion}
      withMinHeight={withMinHeight}
      isStepperActive={isStepperActive}
      variation={variation}
      isMutedStateEnabled={isMutedStateEnabled}
      allowsOnlySinglePortion={maxPortionsForGroup === 1}
      maxPortionsForGroup={maxPortionsForGroup}
      portionOptions={portionOptions}
      hasPortionOptions={hasPortionOptions}
      isUpsellPricingEnabled={isUpsellPricingEnabled}
      onIncrement={onIncrement}
      onDecrement={onDecrement}
      onRemove={onRemove}
      onToggleSplitPortion={onToggleSplitPortion}
      setActiveStepperId={setActiveStepperId}
      resetStepperTimeout={resetStepperTimeout}
      clearStepperTimeout={clearStepperTimeout}
      size={size}
      dataSet={dataSet}
    />
  );
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  modification: CustomizationMachineTypes.ProductModification;
  quantity: number;
  isSelected: boolean;
  isStepperActive: boolean;
  notice?: string;
  upsellPrice?: number;
  withMinHeight?: boolean;
  assetAspectRatioY?: number;
  assetAspectRatioX?: number;
  variation?: 'card' | 'list-item';
  canAddMore: boolean;
  isMutedStateEnabled?: boolean;
  maxPortionsForGroup: number;
  portionOptions?: PortionOption[];
  hasPortionOptions?: boolean;
  isUpsellPricingEnabled?: boolean;
  isSplitPortion?: boolean;
  size?: 'sm' | 'md';
  dataSet?: Record<string, string | number>;
  onIncrement: (ingredientId: string) => void;
  onDecrement: (ingredientId: string) => void;
  onRemove?: (ingredientId: string) => void;
  onToggleSplitPortion?: (ingredientId: string) => void;
  setActiveStepperId: (stepperId: string | null) => void;
  resetStepperTimeout: () => void;
  clearStepperTimeout: () => void;
};
